import { Component, inject, input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { CartService } from '@core/services/cart/cart.service';
import { OrdersService } from '@core/services/orders/orders.service';
import { BasketService } from '@core/services/basket/basket.service';
import { PaymentStoreDispatcher } from '@common/dispatchers/payment.dispatcher';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { of, throwError, timer } from 'rxjs';
import { DoodOrderModel } from '@store/order/order.model';
import { OrderKeys } from '@config/keys/order.keys';
import { catchError, takeUntil, tap } from 'rxjs/operators';
import { PaymentMethodHandles } from '@config/payment-methods.config';
import { DoodApiError } from '@shared/interfaces/error.interface';
import { Paths } from '@config/paths.config';
import { TcposScoService } from '@core/services/tcpos-sco/tcpos-sco.service';
import { TcposScoPaymentState } from '@core/services/api/tcpos-sco/tcpos-sco-api.service';
import { TranslateService } from '@ngx-translate/core';

export type TcposScoPanelState = 'loading' | 'waiting' | 'success' | 'fail' | 'cancelled' | 'error';

@Component({
  selector: 'app-payment-panel-tcpos-sco',
  templateUrl: './payment-panel-tcpos-sco.component.html',
  styles: [],
  standalone: false,
})
export class PaymentPanelTcposScoComponent extends DestroyerBase implements OnInit, OnDestroy {
  readonly terminalInstructionMessage = input<string>();

  panelState: TcposScoPanelState = 'loading';
  statusMessage = '';
  errorMessage = '';
  loadKioskResetTimer = false;

  private paidOrder?: DoodOrderModel;
  private agentOrderId?: string;
  private isPaid = false;

  private readonly router = inject(Router);
  private readonly cartService = inject(CartService);
  private readonly ordersService = inject(OrdersService);
  private readonly basketService = inject(BasketService);
  private readonly paymentDispatcher = inject(PaymentStoreDispatcher);
  private readonly tcposScoService = inject(TcposScoService);
  private readonly routerHelper = inject(RouterHelperService);
  private readonly translate = inject(TranslateService);

  ngOnInit(): void {
    timer(1).subscribe(() => {
      this.paymentDispatcher.updateUI({
        error: null,
        button: {
          isEnabled: false,
          isVisible: false,
        },
        status: {
          isLoading: false,
        },
      });
    });

    this.createOrderAndPay();
  }

  ngOnDestroy(): void {
    if (!this.isPaid && this.agentOrderId) {
      // Fire-and-forget: do not takeUntil(_destroyerRef) or the cancel request would be aborted
      this.tcposScoService.cancelPayment$(this.agentOrderId).subscribe();
    }

    super.ngOnDestroy();
  }

  createOrderAndPay(): void {
    this.panelState = 'loading';
    this.errorMessage = '';
    this.statusMessage = '';
    this.loadKioskResetTimer = false;

    this.ordersService
      .createOrder$(PaymentMethodHandles.TcposSco)
      .pipe(
        takeUntil(this._destroyerRef),
        tap(order => {
          if (!order) {
            return;
          }

          this.paidOrder = order;
          this.agentOrderId = order[OrderKeys.Id];
          this.startPayment(order);
        }),
        catchError((response: { error: DoodApiError }) => {
          this.handleOrderError(response.error);
          return throwError(() => response);
        }),
      )
      .subscribe();
  }

  retryPayment(): void {
    if (!this.agentOrderId) {
      this.createOrderAndPay();
      return;
    }

    this.panelState = 'waiting';
    this.errorMessage = '';
    this.statusMessage = this.translate.instant('payment.tcpos-sco.waiting');
    this.loadKioskResetTimer = false;
    this.runPaymentFlow(this.agentOrderId);
  }

  cancelPayment(): void {
    if (!this.agentOrderId) {
      this.backToCart();
      return;
    }

    this.tcposScoService
      .cancelPayment$(this.agentOrderId)
      .pipe(
        takeUntil(this._destroyerRef),
        catchError(() => of(null)),
      )
      .subscribe(() => {
        this.panelState = 'cancelled';
        this.errorMessage = this.translate.instant('payment.tcpos-sco.cancelled');
        this.loadKioskResetTimer = true;
      });
  }

  backToCart(): void {
    this.router.navigate([this.routerHelper.translateRoute(`${Paths.Cart}`)]);
  }

  private startPayment(order: DoodOrderModel): void {
    const finalPrice = order[OrderKeys.FinalPrice];

    if (typeof finalPrice !== 'number') {
      this.panelState = 'error';
      this.errorMessage = this.translate.instant('payment.tcpos-sco.invalid-amount');
      return;
    }

    this.agentOrderId = order[OrderKeys.Id];
    this.panelState = 'waiting';
    this.statusMessage = this.translate.instant('payment.tcpos-sco.waiting');
    this.runPaymentFlow(this.agentOrderId);
  }

  private runPaymentFlow(agentOrderId: string): void {
    this.tcposScoService
      .runPaymentFlow$(agentOrderId)
      .pipe(takeUntil(this._destroyerRef))
      .subscribe({
        next: state => this.handlePaymentState(state),
        error: () => {
          this.panelState = 'error';
          this.errorMessage = this.translate.instant('payment.tcpos-sco.agent-error');
          this.loadKioskResetTimer = true;
        },
      });
  }

  private handlePaymentState(state: TcposScoPaymentState): void {
    switch (state) {
      case 'success':
        this.handlePaymentSuccess();
        break;
      case 'fail':
        this.panelState = 'fail';
        this.statusMessage = '';
        this.errorMessage = this.translate.instant('payment.tcpos-sco.fail');
        this.loadKioskResetTimer = true;
        break;
      case 'error':
        this.panelState = 'error';
        this.statusMessage = '';
        this.errorMessage = this.translate.instant('payment.tcpos-sco.terminal-error');
        this.loadKioskResetTimer = true;
        break;
      default:
        this.panelState = 'waiting';
        this.statusMessage = this.translate.instant('payment.tcpos-sco.waiting');
        break;
    }
  }

  private handlePaymentSuccess(): void {
    if (!this.paidOrder) {
      return;
    }

    this.isPaid = true;
    this.panelState = 'success';
    this.statusMessage = this.translate.instant('payment.tcpos-sco.success');
    this.errorMessage = '';
    this.basketService.clearBasket();
    this.cartService.clearCart();
    this.router.navigate([
      this.routerHelper.translateRoute(`${Paths.Orders}/${this.paidOrder.id}/${Paths.Status}`),
    ]);
  }

  private handleOrderError(error: Partial<DoodApiError> & { error?: string }): void {
    // INPUT_DATA_ERRORS (e.g. PRODUCT_NOT_AVAILABLE) are handled in OrdersService:
    // violations are stored for the cart error banner and user is redirected to cart.
    if (error?.error === 'INPUT_DATA_ERRORS') {
      this.backToCart();
      return;
    }

    this.panelState = 'error';
    this.errorMessage = error.detail ?? this.translate.instant('payment.tcpos-sco.order-error');
    this.loadKioskResetTimer = true;
  }
}
